/**
 * useNoteGrips
 *
 * Lädt den Harmonika-Griff (zugewiesen + Alternativen) einer angeklickten Note der
 * Griffschrift-**Ausgabe** via `POST /api/note-grips`. Reaktiv auf `musicXml`/`selectedNote`,
 * debounced (200 ms). Pausen/keine Auswahl → `response` = `null` (kein Request). Extrahiert aus
 * `NoteGripInspector.vue`, damit der Inspector – gehostet im gemeinsamen `NoteGripDialog`
 * (Toolbar- und Rechtsklick-Weg) – die Fetch-Logik gekapselt hält.
 *
 * Der Fetch läuft über einen geteilten **@tanstack/vue-query**-Cache (`VueQueryPlugin` in
 * `main.ts`): alle Konsumenten (Inspector, Kontextmenü-Info-Zeile, Hover-Tooltip) treffen für
 * dieselbe Note derselben Dokument-Fassung EINEN Request. Der Query-Key trägt statt des
 * 100–500 KB großen `musicXml`-Strings eine **Generation** (kleine Zahl, Referenzvergleich wie
 * `soundingNoteCache`) plus die Noten-Adresse; `staleTime: Infinity`, weil die Antwort pro Key
 * deterministisch ist. Abbruch laufender Requests übernimmt vue-query über das konsumierte
 * `{signal}` (Key-Wechsel + Scope-Dispose); abgebrochene Requests erzeugen keinen Fehler.
 */
import {computed, type MaybeRefOrGetter, type ShallowRef, shallowRef, toValue, watch} from 'vue';
import {watchDebounced} from '@vueuse/core';
import {useQuery, useQueryClient} from '@tanstack/vue-query';

import {apiClient} from '@/api-client';
import type {NoteInfo} from '@/composables/musicXmlDom';

import type {NoteGripsRequest, NoteGripsResponse} from '@/components/harmonika/gripTypes';
import {buildNoteGripsRequest} from '@/components/harmonika/noteGrips';

export const NOTE_GRIPS_QUERY_PREFIX = 'note-grips';

let generationXml: string | null = null;
let generationId = 0;

/**
 * musicXml-Identität → kleine Zahl im Query-Key (Referenzvergleich auf den durchgereichten
 * String, kein Hashing – dasselbe Muster wie `soundingNoteCache`). Neue Fassung = neue
 * Generation; Einträge alter Generationen sind damit unerreichbar und werden im Aufrufer
 * aktiv aus dem Query-Cache entfernt (die gecachten `queryFn`-Closures hielten sonst den
 * alten XML-String fest).
 */
export function noteGripsGeneration(xml: string): number {
    if (xml !== generationXml) {
        generationXml = xml;
        generationId += 1;
    }
    return generationId;
}

export interface UseNoteGrips {
    response: ShallowRef<NoteGripsResponse | null>;
    /** Die Note, zu der `response` gehört – `null`, solange keine Antwort vorliegt.
     *
     *  Antworten treffen asynchron ein: zwischen Request-Start (nach dem Debounce) und Antwort ist
     *  die Auswahl bzw. der Hover-Zeiger oft schon weiter. Ein Konsument, der `response` blind der
     *  **aktuellen** Note zuschreibt, zeigt damit den klingenden Ton einer anderen Note an. Wer
     *  `response` einer Note zuordnet, MUSS deshalb über dieses Feld gehen. */
    requestedNote: ShallowRef<NoteInfo | null>;
    loading: ShallowRef<boolean>;
    error: ShallowRef<string | null>;
}

interface FetchTarget {
    note: NoteInfo;
    request: NoteGripsRequest;
    key: readonly (string | number)[];
}

export function useNoteGrips(
    musicXml: MaybeRefOrGetter<string | undefined>,
    selectedNote: MaybeRefOrGetter<NoteInfo | null | undefined>,
): UseNoteGrips {
    const response = shallowRef<NoteGripsResponse | null>(null);
    const requestedNote = shallowRef<NoteInfo | null>(null);
    const loading = shallowRef(false);
    const error = shallowRef<string | null>(null);

    const queryClient = useQueryClient();
    const target = shallowRef<FetchTarget | null>(null);

    // Der 200-ms-Debounce bleibt HIER (vue-query hat keinen): erst das entprellte target speist
    // den Query-Key. `buildNoteGripsRequest` (inkl. DOMParser-Lauf) läuft damit weiterhin nur
    // einmal pro beruhigter Auswahl.
    watchDebounced(
        () => [toValue(musicXml), toValue(selectedNote)] as const,
        ([xml, note]) => {
            const request = xml ? buildNoteGripsRequest(xml, note) : null;
            if (!request || !note) {
                target.value = null;
                return;
            }
            const generation = noteGripsGeneration(xml!);
            // Alte Generationen sind unerreichbar → aktiv entfernen (Speicher-Obergrenze).
            // Der `'idle'`-Platzhalter-Key (queryKey[1] ist kein number) bleibt unberührt.
            queryClient.removeQueries({
                queryKey: [NOTE_GRIPS_QUERY_PREFIX],
                predicate: (q) => typeof q.queryKey[1] === 'number' && q.queryKey[1] !== generation,
            });
            target.value = {
                note,
                request,
                key: [
                    NOTE_GRIPS_QUERY_PREFIX,
                    generation,
                    request.part_index ?? 0,
                    request.measure_number,
                    request.measure_index ?? -1,
                    request.voice ?? 1,
                    request.voice_note_index,
                ],
            };
        },
        {debounce: 200, immediate: true},
    );

    const query = useQuery(
        computed(() => {
            const t = target.value;
            return {
                queryKey: t ? t.key : ([NOTE_GRIPS_QUERY_PREFIX, 'idle'] as const),
                enabled: t !== null,
                // `{signal}` konsumieren = vue-query bricht den Request bei Key-Wechsel und
                // Scope-Dispose ab (ersetzt den früheren manuellen AbortController).
                queryFn: ({signal}: {signal: AbortSignal}) => {
                    const r = t!.request;
                    return apiClient.noteGrips(
                        r.music_xml,
                        r.measure_number,
                        r.voice_note_index,
                        r.voice ?? 1,
                        r.part_index ?? 0,
                        {signal},
                        r.measure_index ?? undefined,
                    );
                },
                staleTime: Infinity, // Antwort ist pro Key deterministisch (Generation = Dokument-Fassung)
                gcTime: 5 * 60_000,
                retry: false as const, // ein Versuch wie bisher; Default 3 verfälschte Fehlerzeit + Call-Zählungen
                refetchOnWindowFocus: false,
            };
        }),
        queryClient,
    );

    // Inhalt AUTORITATIV aus dem Query-Cache lesen (synchron); die Observer-Refs dienen nur als
    // Trigger: `notifyManager` batcht ihre Updates (setTimeout 0), `query.data` kann dem `target`
    // also einen Macrotask hinterherhinken. Wer `query.data` als Inhalt läse, hängte die Antwort
    // von Note A an die Identität von Note B – `getQueryData(t.key)` kann das strukturell nicht.
    const sync = () => {
        const t = target.value;
        if (!t) {
            response.value = null;
            requestedNote.value = null;
            error.value = null;
            loading.value = false;
            return;
        }
        const data = queryClient.getQueryData<NoteGripsResponse>(t.key);
        if (data !== undefined) {
            // Zusammen setzen: `response` und die Note, für die sie erfragt wurde.
            requestedNote.value = t.note;
            response.value = data;
            error.value = null;
            loading.value = false;
            return;
        }
        const state = queryClient.getQueryState(t.key);
        if (state?.status === 'error' && state.error) {
            response.value = null;
            requestedNote.value = null;
            loading.value = false;
            error.value = state.error instanceof Error ? state.error.message : 'Griff konnte nicht ermittelt werden.';
            return;
        }
        // In-flight: vorige response/requestedNote stehen lassen (bisheriges Verhalten).
        loading.value = true;
        error.value = null;
    };
    watch([target, query.data, query.error, query.fetchStatus], sync, {immediate: true});

    return {response, requestedNote, loading, error};
}
